import { ContentPreview } from "@turtlewave/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { contentDetailPath } from "../../utils/navigation";

import { MediumPill } from "./MediumPill";

interface ContentCardProps {
  content: ContentPreview;
  sizeClassName?: string;
  compilationId?: string;
}

const MONTH_SHORTHAND: Record<number, string> = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

export const ContentCard = ({
  content,
  sizeClassName,
  compilationId,
}: ContentCardProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [pillsClipped, setPillsClipped] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const fitTitle = useCallback(() => {
    const el = titleRef.current;
    if (!el) return;
    const maxSize = 36;
    const minSize = 24;
    el.style.fontSize = `${maxSize}px`;
    el.style.whiteSpace = "nowrap";
    let size = maxSize;
    while (el.scrollWidth > el.clientWidth && size > minSize) {
      size -= 0.5;
      el.style.fontSize = `${size}px`;
    }
    if (el.scrollWidth > el.clientWidth) {
      el.style.whiteSpace = "";
    }
  }, []);

  const updateLayout = useCallback(() => {
    fitTitle();
    const pills = pillsRef.current;
    if (pills) setPillsClipped(pills.scrollWidth > pills.clientWidth);
  }, [fitTitle]);

  useEffect(() => {
    updateLayout();
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateLayout);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateLayout]);

  const formattedDate = content.publish_date.month
    ? `${MONTH_SHORTHAND[content.publish_date.month]} ${content.publish_date.year}`
    : `${content.publish_date.year}`;

  return (
    <Link
      to={contentDetailPath(content.slug, compilationId)}
      state={{
        from: location.pathname,
        scrollY: typeof window === "undefined" ? 0 : window.scrollY,
      }}
      className={sizeClassName ? "block h-full w-full" : undefined}
    >
      <div
        ref={cardRef}
        className={`content-card-container group relative isolate transition-all duration-300 ease-out ${sizeClassName || `h-card max-w-card-lg ${!aspectRatio ? "w-card-sm" : ""}`} ${isPressed && "scale-97 shadow-xl shadow-blue-950/20"}`}
        style={{
          ...(aspectRatio ? { aspectRatio } : {}),
        }}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
      >
        <div className="content-card relative h-full w-full overflow-hidden rounded-xs shadow-lg">
          <img
            src={content.preview_url ?? undefined}
            alt={content.title}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              if (naturalHeight > 0)
                setAspectRatio(naturalWidth / naturalHeight);
            }}
            className="absolute inset-0 z-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-103"
          />

          {/* Inset shadow overlay */}
          <div className="shadow-inner-lg pointer-events-none absolute inset-0 z-1" />

          <div className="absolute inset-0 z-5 bg-black/50 opacity-0 backdrop-blur-[0.5px] transition-opacity duration-300 ease-out group-hover:opacity-100" />

          <div className="absolute inset-x-0 -bottom-2 z-10 p-6 pt-10 opacity-0 transition-all duration-300 ease-out group-hover:opacity-100 md:-bottom-10 md:group-hover:-translate-y-8">
            <h3
              ref={titleRef}
              className="mb-2 truncate font-serif text-4xl font-normal text-white"
            >
              {content.title}
            </h3>

            <div className="flex items-center gap-3 overflow-hidden text-sm whitespace-nowrap text-white/90">
              <span>{formattedDate}</span>

              <div
                ref={pillsRef}
                className="flex gap-1.5 overflow-hidden"
                style={
                  pillsClipped
                    ? {
                        maskImage:
                          "linear-gradient(to right, black calc(100% - 1.5rem), transparent)",
                      }
                    : undefined
                }
              >
                {content.medium.map((med, index) => (
                  <MediumPill key={index} medium={med} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
